import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetMeQuery,
  useUpdateMeMutation,
  useCompleteUserOnboardingMutation,
  useGetVoiceConfigQuery,
} from '@/redux/internal-services/usersApi';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { setUserOnboardingCompleted, setPendingVoiceHandoff } from '@/redux/slices/sessionStateSlice';
import { useGetOnboardingStateQuery, useGetConfigurationQuery, useUpdateUserStepMutation, useGetTenantSettingsQuery } from '@/redux/internal-services/onboardingApi';
import { useGetBrandConfigQuery } from '@/redux/internal-services/brandApi';
import { useIsTenantAdmin } from '@/hooks/useIsTenantAdmin';
import { useOnboardingVoice } from '@/hooks/useOnboardingVoice';
import { socketService } from '@/services/SocketService';
import { VoiceModeButton } from '@/components/Onboarding/VoiceModeButton';
import OnboardingStepNav from '@/components/Onboarding/OnboardingStepNav';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import ProfileStep from '@/components/Onboarding/UserOnboarding/ProfileStep';
import PhoneVerifyStep from '@/components/Onboarding/UserOnboarding/PhoneVerifyStep';
import VoiceModeStep from '@/components/Onboarding/UserOnboarding/VoiceModeStep';
import VoiceIntroStep from '@/components/Onboarding/VoiceIntroStep';
import UserConfirmationStep from '@/components/Onboarding/UserOnboarding/UserConfirmationStep';
import { buildVoicePrefsPayload } from './voicePrefsPayload';

type Screen = 'voice_intro' | 'profile' | 'verify-phone' | 'voice' | 'confirmation';

// Session guard key so the SAM intro voice auto-starts exactly once per onboarding session
// (survives remounts + re-renders), cleared when the page unmounts.
const VOICE_STARTED_KEY = 'uo_voice_autostarted';

const ic = (path: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">{path}</svg>
);

const DEFAULT_ICON = <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><circle cx="10" cy="10" r="8" /></svg>;

// Presentational icons keyed by step id — the labels/order/required come from the backend
// `user_steps` (onboarding config), keeping this rail data-driven and consistent with the
// company-setup wizard.
const STEP_ICONS: Record<string, React.ReactNode> = {
  profile: ic(<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  voice: ic(<><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4" /></>),
};

const KNOWN_USER_STEPS = new Set<string>(['profile', 'voice']);

// Fallback rail if the backend omits `user_steps` (older API, or a user lacking tenant:read
// on the state endpoint). Matches the system-layer config defaults.
const USER_STEPS_FALLBACK = [
  { id: 'profile', label: 'Your Profile' },
  { id: 'voice', label: 'Voice & Interaction' },
];

/**
 * Per-user onboarding — runs after company setup for every user, styled as a continuation of
 * the setup wizard (gradient + left step rail + right content card). Two steps: profile, then
 * voice/mode. On completion the user lands on the dashboard. Redirects out if already complete.
 */
const UserOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isTenantAdmin = useIsTenantAdmin();
  const alreadyDone = useAppSelector((s) => s.sessionState.userOnboardingCompleted);
  // Once SAM welcome has fired for this user, per-user onboarding is off-limits — a returning
  // member typing /onboarding/profile in the URL must bounce straight to /chat, not resume the
  // wizard for a lingering voice/checklist step. Persistent flag on tenant_memberships.
  const samWelcomeShown = useAppSelector((s) => s.sessionState.samWelcomeShown);
  // While the voice agent is driving the step, disable manual editing (SAM fills the fields).
  const uoVoiceStatus = useAppSelector((s) => s.onboardingVoice.voiceStatus);
  const isVoiceActive = uoVoiceStatus !== 'idle' && uoVoiceStatus !== 'error';
  // Role names are resolved at token-build time via get_by_id (works for all roles regardless
  // of tenant query issues) and stored in the session. Read directly — no extra API call needed.
  const roleNames = useAppSelector((s) => s.sessionState.roleNames);
  const roleName = roleNames[0] ?? '';

  const { data: me } = useGetMeQuery();
  const { data: voiceConfig } = useGetVoiceConfigQuery();
  // Sidebar rail steps are data-driven from the backend `user_steps`; fall back to the hardcoded
  // pair if the state endpoint is unavailable (older API or user lacking tenant:read).
  const { data: onboardingState } = useGetOnboardingStateQuery();
  // Load user_setup_steps from the config catalogue — source of truth for per-user step
  // definitions. The onboarding state endpoint also returns these, but pulling directly from
  // the catalogue keeps this in sync with how all other configurable step lists are loaded.
  const { data: onboardingConfig } = useGetConfigurationQuery('onboarding');
  // Brand + tenant — shown on the VoiceIntroStep welcome card.
  const { data: brandConfig } = useGetBrandConfigQuery();
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const companyName = (tenantSettings?.data?.company_name as string | undefined) ?? '';
  const logoUrl = brandConfig?.logo_light_url ?? '';
  // Text-to-SAM inline input — shown inside the VoiceModeButton chip when enabled.
  // Distinct config key from chat_settings.interactive_text_input_enabled (a separate flag for
  // the main chat page) — do not confuse the two.
  // Pushed once by the backend on the "onboarding:config" socket event at voice-session start
  // (see OnboardingSessionManager.start()) — not a REST call, and not required for the session
  // to work: if the event is never received the flag just stays false (safe default).
  const interactiveTextInputEnabled = useAppSelector((state) => state.onboardingVoice.debugTextInputEnabled);
  const steps = useMemo(() => {
    const catalogueUserSteps = onboardingConfig?.data?.effective_value?.user_setup_steps as
      | { id: string; label: string; required: boolean }[]
      | undefined;
    const userSteps = catalogueUserSteps?.length
      ? catalogueUserSteps.filter((s) => KNOWN_USER_STEPS.has(s.id)).map((s) => ({ id: s.id, label: s.label }))
      : USER_STEPS_FALLBACK;
    return userSteps.map((s) => ({ ...s, icon: STEP_ICONS[s.id] ?? DEFAULT_ICON }));
  }, [onboardingConfig]);
  const [updateMe, { isLoading: saving }] = useUpdateMeMutation();
  const [updateUserStep] = useUpdateUserStepMutation();
  const [completeOnboarding, { isLoading: finishing }] = useCompleteUserOnboardingMutation();

  // Fresh members start on the SAM welcome (voice_intro); the resume effect below jumps returning
  // users straight to whichever step is still outstanding.
  const [screen, setScreen] = useState<Screen>('voice_intro');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  // Controlled phone draft — lifted so the voice agent can fill it via set_profile_field.
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [mode, setMode] = useState<'text' | 'voice'>('voice');
  const [voiceGender, setVoiceGender] = useState<'male' | 'female' | null>('female');
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (alreadyDone || samWelcomeShown) navigate('/chat', { replace: true });
  }, [alreadyDone, samWelcomeShown, navigate]);

  useEffect(() => {
    if (hydrated || !me) return;
    setName(me.name ?? '');
    setEmail(me.email);
    setPhone(me.phone);
    setPhoneVerified(me.phone_verified);
    setAvatarUrl(me.profile_picture_url ?? '');
    // Onboarding pre-selects Voice & Text with a female voice. Only honor a previously-saved
    // choice once onboarding is complete (a returning user) — a fresh user's backend default is
    // 'text', which must not override the intended voice-first default here.
    setMode(me.onboarding_completed && me.interaction_mode ? me.interaction_mode : 'voice');
    setVoiceGender(me.onboarding_completed && me.voice_gender ? me.voice_gender : 'female');
    setHydrated(true);
  }, [me, voiceConfig, hydrated]);

  // Resume at the first unresolved user step based on backend-tracked status.
  // Runs once after both hydration and onboarding state are available.
  const [screenResumed, setScreenResumed] = useState(false);
  useEffect(() => {
    if (screenResumed || !hydrated || !onboardingState?.data) return;
    const firstUnresolved = onboardingState.data.user_steps?.find(
      (s) => s.status !== 'completed' && s.status !== 'skipped'
    );
    // Profile already done, voice still pending → skip the intro and resume at the voice step.
    // Otherwise (profile outstanding) leave the fresh member on the voice_intro welcome. Flag
    // resume as settled either way so the intro auto-start (gated on this) can proceed.
    if (firstUnresolved && firstUnresolved.step === 'voice') {
      setScreen('voice');
    }
    setScreenResumed(true);
  }, [screenResumed, hydrated, onboardingState]);

  // Save name/picture, then either go verify a new phone number or straight to the voice step.
  const handleProfileProceed = async (newPhone: string | null) => {
    await updateMe({ name: name.trim() || undefined, profile_picture_url: avatarUrl || undefined }).unwrap().catch(() => {});
    if (newPhone) {
      setPendingPhone(newPhone);
      setScreen('verify-phone');
    } else {
      await updateUserStep({ step: 'profile', status: 'completed' }).catch(() => {});
      setScreen('voice');
    }
  };

  const handlePhoneVerified = () => {
    setPhone(pendingPhone);
    setPhoneVerified(true);
    void updateUserStep({ step: 'profile', status: 'completed' }).catch(() => {});
    setScreen('voice');
  };

  // wasVoiceActive: was an onboarding voice session live at the moment the user confirmed —
  // captured by the CALLER before it stops the session (stopVoice flips isVoiceActive false), not
  // read fresh in here. Carries through as pendingVoiceHandoff so ChatPage auto-starts interactive
  // mode instead of landing in text mode. Mirrors SetupWizardPage.handleFinishOnboarding: without
  // it, invited users (PM/sales/office/crew) finished onboarding by voice and still landed in the
  // text composer, while tenant admins came through in interactive mode.
  const handleFinish = useCallback(async (wasVoiceActive = false) => {
    await updateMe(buildVoicePrefsPayload(mode, voiceGender)).unwrap().catch(() => {});
    await completeOnboarding().unwrap().catch(() => {});
    dispatch(setUserOnboardingCompleted(true));
    if (wasVoiceActive) dispatch(setPendingVoiceHandoff(true));
    navigate('/chat', { replace: true });
  }, [updateMe, completeOnboarding, dispatch, navigate, mode, voiceGender]);

  // Manual "Finish Setup" on the voice step → show the summary screen first (the same screen the
  // voice flow lands on), rather than jumping straight to chat. The summary's "Let's Go!" finishes.
  const handleGoToConfirmation = useCallback(async () => {
    await updateMe(buildVoicePrefsPayload(mode, voiceGender)).unwrap().catch(() => {});
    setScreen('confirmation');
  }, [updateMe, mode, voiceGender]);

  // Stable ref for current voice-step state (used by the voice agent for state_sync).
  const screenRef = useRef(screen);
  const stateRef = useRef({
    name, phoneValue, avatarUrl, mode, voiceGender,
    email: '' as string,
  });
  useEffect(() => { screenRef.current = screen; }, [screen]);
  useEffect(() => {
    stateRef.current = {
      name, phoneValue, avatarUrl, mode, voiceGender,
      email: me?.email ?? '',
    };
  }, [name, phoneValue, avatarUrl, mode, voiceGender, me]);

  const getStateSnapshot = useCallback(() => {
    const { name: n, phoneValue: pv, avatarUrl: av, mode: m, voiceGender: vg, email } = stateRef.current;
    if (screenRef.current === 'profile') {
      return { fullName: n, email, phone: pv, avatarUrl: av };
    }
    // Include the pickable voices so SAM can name the options and confirm the user's choice.
    return { interactionMode: m === 'voice' ? 'voice_and_text' : 'text_only', voiceGender: vg ?? '' };
  }, []);

  // ── Voice agent integration ──────────────────────────────────────────────
  // Stable refs so `nav` (defined before the hook returns) can reach start/stop without a cycle.
  const stopVoiceRef = useRef<() => void>(() => {});
  const startVoiceRef = useRef<() => void>(() => {});
  const { startVoice, stopVoice, sendStateSync, isMuted, toggleMute, isSpeakerMuted, toggleSpeakerMute, showTranscript, toggleTranscript, sendText } = useOnboardingVoice(
    () => {
      const s = screenRef.current;
      // voice_intro is a client-only welcome step; the backend injects its intro agent, which then
      // hands off to the first real step (profile) for members.
      if (s === 'voice_intro') return { currentStep: 'voice_intro', completedSteps: [] };
      if (s === 'voice' || s === 'confirmation') return { currentStep: 'voice', completedSteps: ['profile'] };
      return { currentStep: 'profile', completedSteps: [] };
    },
    () => null, // no StepHandle refs — state is supplied via userStepCallbacks.getState
    {
      // From voice_intro → go to profile (invited users skip the voice setup step).
      // From profile → go to voice setup step as normal.
      goNext: () => { if (screenRef.current === 'voice_intro') setScreen('profile'); else setScreen('voice'); },
      goBack: () => setScreen('profile'),
      skipStep: () => {},
      // SAM's skip_to_dashboard() — only reachable from a live voice session, so hand off to
      // interactive mode on /chat rather than dropping the user into the text composer.
      skipToDashboard: () => {
        stopVoiceRef.current();
        dispatch(setPendingVoiceHandoff(true));
        navigate('/chat', { replace: true });
      },
      launch: () => {},
      // SAM's go_to_profile()/go_to_voice() land here (via navigate_to_step) — drive the screen.
      goToStep: (s) => { if (s === 'profile') setScreen('profile'); else if (s === 'voice') setScreen('voice'); },
      // Completion agent confirmed on the summary screen — end voice, then finish for real.
      // This callback only ever fires via the voice agent's own open_dashboard tool, which needs a
      // live voice session to reach at all — so wasVoiceActive is unconditionally true here.
      openDashboard: () => { stopVoiceRef.current(); void handleFinish(true); },
    },
    {
      // getState is used by the hook's sendStateSync to include current step state in
      // state_sync messages (no StepHandle ref here). Field mutations are handled by
      // the direct socket listener below instead of these callbacks.
      getState: getStateSnapshot,
    },
  );
  useEffect(() => { stopVoiceRef.current = stopVoice; }, [stopVoice]);
  useEffect(() => { startVoiceRef.current = startVoice; }, [startVoice]);

  // Auto-start SAM voice ONCE when a fresh member lands on the welcome screen — SAM greets
  // them with the company name. The ref + sessionStorage double-guard prevents re-triggering.
  const voiceAutoStartedRef = useRef(false);
  useEffect(() => {
    // Wait for resume to settle so a returning member resumed to a later step never briefly
    // triggers the intro greeting before being redirected.
    if (!screenResumed || screen !== 'voice_intro') return;
    if (voiceAutoStartedRef.current) return;
    voiceAutoStartedRef.current = true;
    sessionStorage.setItem(VOICE_STARTED_KEY, '1');
    void startVoiceRef.current();
  }, [screen, screenResumed]);
  // Clear the guard on unmount so a future onboarding session gets a fresh auto-start.
  useEffect(() => () => { sessionStorage.removeItem(VOICE_STARTED_KEY); }, []);

  // Push a state_sync the instant the screen changes so the voice agent knows a navigation landed
  // (its transition tools poll for this). No-ops when no voice session is active.
  useEffect(() => { sendStateSync(); }, [screen, sendStateSync]);

  // ── Direct field-update listener ────────────────────────────────────────────
  // The voice agent dispatches fill_field / set_interaction_mode / set_voice_gender / finish
  // as `onboarding:action` socket events. We handle field mutations directly here so
  // state setters are called from this component's own closure — guaranteed to update
  // the right component's state. Navigation (`navigate` action) is handled by the hook
  // via the `nav` callbacks; audio/status/transcript are also handled by the hook.
  useEffect(() => {
    const onAction = (data: unknown) => {
      const msg = data as Record<string, unknown>
      const action = msg.action as string
      const target = msg.target as string
      const value = msg.value as string

      switch (action) {
        case 'fill_field':
          if (target === 'fullName') setName(value)
          else if (target === 'phone') setPhoneValue(value)
          else if (target === 'phoneCountryCode') setPhoneCountryCode(value)
          break
        case 'set_interaction_mode':
          setMode(value === 'voice_and_text' ? 'voice' : 'text')
          break
        case 'set_voice_gender':
          if (value === 'male' || value === 'female') setVoiceGender(value)
          break
        case 'finish':
          // SAM finished — persist the interaction choice, then show the summary screen (voice
          // stays live). The real finish happens on the summary's "Let's Go!" or SAM's open_dashboard.
          void updateMe(buildVoicePrefsPayload(stateRef.current.mode, stateRef.current.voiceGender))
            .unwrap().catch(() => {})
          setScreen('confirmation')
          break
        default:
          break
      }
    }
    socketService.on('onboarding:action', onAction)
    return () => socketService.off('onboarding:action', onAction)
  }, [updateMe]) // setName/setPhoneValue/setMode/setVoiceGender are stable setState setters

  // Cleanup on unmount
  useEffect(() => () => stopVoice(), []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-dvh overflow-hidden flex bg-ui">
      {/* Left panel — full height; its step-list scrolls internally (scrollbar hidden) so the
          logo/heading never move. Fixed-height shell avoids position:sticky (broken by body's
          overflow-x:hidden). */}
      <div className="hidden md:block h-dvh shrink-0">
        <LeftSidePanel className="h-full">
          <OnboardingStepNav
            heading="Finish setting up your profile"
            subtitle="Just a couple of steps so SAM knows who you are."
            steps={steps}
            activeId={screen === 'voice' || screen === 'confirmation' ? 'voice' : 'profile'}
          />
        </LeftSidePanel>
      </div>

      {/* Right panel — scrolls internally when the form is taller than the viewport. */}
      <div className="flex-1 flex flex-col bg-ui min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto no-scrollbar">
          <div className="min-h-full flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className={`w-full ${screen === 'voice' ? 'max-w-2xl' : 'max-w-lg'} mx-auto`}>
            {screen === 'voice_intro' && (
              <VoiceIntroStep
                voiceStatus={uoVoiceStatus}
                isVoiceActive={isVoiceActive}
                onManual={() => { stopVoice(); setScreen('profile'); }}
                companyName={companyName || undefined}
                logoUrl={logoUrl || undefined}
              />
            )}
            {screen === 'profile' && (
              <ProfileStep
                name={name}
                email={email}
                roleName={roleName}
                phone={phone}
                phoneVerified={phoneVerified}
                avatarUrl={avatarUrl}
                onNameChange={setName}
                onAvatarChange={setAvatarUrl}
                onProceed={handleProfileProceed}
                phoneValue={phoneValue}
                onPhoneValueChange={setPhoneValue}
                countryCodeValue={phoneCountryCode}
                onBack={isTenantAdmin ? () => navigate('/onboarding/setup', { state: { step: 'team_onboarding' } }) : undefined}
                saving={saving}
                disabled={isVoiceActive}
              />
            )}
            {screen === 'verify-phone' && (
              <PhoneVerifyStep
                phone={pendingPhone}
                onVerified={handlePhoneVerified}
                onBack={() => setScreen('profile')}
              />
            )}
            {screen === 'voice' && (
              <VoiceModeStep
                mode={mode}
                voiceGender={voiceGender}
                onModeChange={setMode}
                onVoiceGenderChange={setVoiceGender}
                onBack={() => setScreen('profile')}
                onFinish={handleGoToConfirmation}
                finishing={saving || finishing}
                disabled={isVoiceActive}
              />
            )}
            {screen === 'confirmation' && (
              <UserConfirmationStep
                name={name}
                roleName={roleName}
                avatarUrl={avatarUrl}
                interactionMode={mode}
                voiceGender={voiceGender}
                onFinish={() => { const wasVoiceActive = isVoiceActive; stopVoice(); void handleFinish(wasVoiceActive); }}
                finishing={saving || finishing}
              />
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Floating voice controls — shown on all screens including the welcome screen (voice
          auto-starts there). On the summary screen, show only while voice is live.
          onSendText wires the text-to-SAM input inside the chip (QA/test only). */}
      {(screen === 'voice_intro' || screen !== 'confirmation' || isVoiceActive) && (
        <VoiceModeButton
          onStart={startVoice}
          onStop={stopVoice}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          isSpeakerMuted={isSpeakerMuted}
          onToggleSpeakerMute={toggleSpeakerMute}
          showTranscript={showTranscript}
          onToggleTranscript={toggleTranscript}
          onSendText={interactiveTextInputEnabled ? sendText : undefined}
        />
      )}
    </div>
  );
};

export default UserOnboardingPage;
